import { useCallback } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedAxiosInstance } from '../api/useAuthenticatedAxiosInstance';
import { getEnvironmentConfig } from '../environmentConfig';

export interface GraphRunSummary {
    id: string;
    graphId: string;
    hasError: boolean;
    duration: number;
    createdAt: string;
}

export interface GraphRunDetail {
    id: string;
    graphId: string;
    userId: string;
    result: unknown | null;
    error: string | null;
    duration: number;
    createdAt: string;
}

interface RunListPage {
    runs: GraphRunSummary[];
    nextCursor: string | null;
}

export const useGraphRuns = (graphId: string) => {
    const api = useAuthenticatedAxiosInstance(getEnvironmentConfig().graphServiceUrl);
    const queryClient = useQueryClient();

    const runsQuery = useInfiniteQuery({
        queryKey: ['graphRuns', graphId],
        queryFn: async ({ pageParam }: { pageParam?: string }) => {
            const params = new URLSearchParams({ limit: '30' });
            if (pageParam) {
                params.set('cursor', pageParam);
            }
            const response = await api.get<RunListPage>(`/graphs/${graphId}/runs?${params.toString()}`);
            return response.data;
        },
        getNextPageParam: (lastPage: RunListPage) => lastPage.nextCursor ?? undefined,
        enabled: !!graphId,
    });

    const fetchRunDetail = useCallback(
        async (runId: string): Promise<GraphRunDetail> => {
            return queryClient.fetchQuery({
                queryKey: ['graphRun', graphId, runId],
                queryFn: async () => {
                    const response = await api.get<GraphRunDetail>(`/graphs/${graphId}/runs/${runId}`);
                    return response.data;
                },
                staleTime: Infinity,
            });
        },
        [graphId, queryClient],
    );

    const invalidateRunList = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['graphRuns', graphId] });
    }, [graphId, queryClient]);

    return {
        runsQuery,
        fetchRunDetail,
        invalidateRunList,
    };
};
